/** Small pieces shared by the Academy screens. */

import { type AcademyAttemptsListExam, useAcademyAttemptsList } from "@agencyos/api-client";
import { TCF_EXAM, isExamKey } from "@agencyos/core";
import { Badge } from "@agencyos/ui-web";
import { Clock3 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { clock } from "./text";
import { type Results, SKILLS, type Skill } from "./types";

/** « 2026-09-25 » or an ISO date-time → « 25/09/2026 » (Africa/Douala). */
export function dateText(value: string | null | undefined, language: string): string {
  if (!value) return "";
  const date = value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(language.startsWith("en") ? "en-GB" : "fr-FR", { timeZone: "Africa/Douala" });
}

/** Whole days from today (Africa/Douala) to a « 2026-11-20 » date; null without a date. */
export function daysUntil(value: string | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  const target = Date.parse(`${value.slice(0, 10)}T00:00:00+01:00`);
  if (Number.isNaN(target)) return null;
  const today = now.toLocaleDateString("en-CA", { timeZone: "Africa/Douala" });
  const start = Date.parse(`${today}T00:00:00+01:00`);
  return Math.round((target - start) / 86_400_000);
}

/** NCLC per skill in a compact row of chips (history, placement). */
export function SkillChips({ results }: { results: Results }) {
  const { t } = useTranslation();
  const shown = SKILLS.filter((skill) => results[skill]);
  if (!shown.length) return null;
  return (
    <ul className="ac-chips" aria-label={t("academy.results.perSkill")}>
      {shown.map((skill) => {
        const row = results[skill];
        return (
          <li key={skill} className={`ac-chip ac-skill--${skill}`}>
            <strong>{t(`academy.skillShort.${skill}`)}</strong>{" "}
            {row?.pending
              ? t("academy.results.pendingShort")
              : t("academy.results.nclcShort", { nclc: row?.nclc ?? 0, cefr: row?.cefr ?? "" })}
          </li>
        );
      })}
    </ul>
  );
}

/** Kind and mode of an attempt: the state endpoint does not repeat them, the list does. The list of
 * the attempt's exam (D-160: the TCF list has no IELTS, TEF or German sitting, whose kind was lost). */
export function useAttemptInfo(id: string, exam?: string): { kind: string; mode: string; loaded: boolean } {
  const params = isExamKey(exam) && exam !== TCF_EXAM ? { exam: exam as AcademyAttemptsListExam } : undefined;
  const list = useAcademyAttemptsList(params, { query: { staleTime: 60_000 } });
  const rows = list.data?.status === 200 ? list.data.data : [];
  const row = rows.find((attempt) => attempt.id === id);
  return { kind: row?.kind ?? "", mode: row?.mode ?? "exam", loaded: list.isSuccess };
}

/**
 * The server keeps the time; the page counts down locally from the value it received and asks
 * the server again when it reaches zero (the section is then closed there).
 */
export function useCountdown(remaining: number, receivedAt: number, onZero: () => void): number {
  const [now, setNow] = useState(() => Date.now());
  const fired = useRef(false);
  const callback = useRef(onZero);
  useEffect(() => {
    callback.current = onZero;
  }, [onZero]);
  useEffect(() => {
    fired.current = false;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [remaining, receivedAt]);
  const left = Math.max(0, remaining - Math.floor((now - receivedAt) / 1000));
  useEffect(() => {
    if (left === 0 && receivedAt > 0 && !fired.current) {
      fired.current = true;
      callback.current();
    }
  }, [left, receivedAt]);
  return left;
}

/** The exam clock: a quiet capsule that turns amber in the last 5 minutes, red in the last one. */
export function Timer({ seconds, label }: { seconds: number; label: string }) {
  const state = seconds <= 60 ? " ac-timer--low" : seconds <= 300 ? " ac-timer--soon" : "";
  return (
    <p className={`ac-timer${state}`}>
      <Clock3 aria-hidden size={15} strokeWidth={2} className="ac-timer__icon" />
      <span className="visually-hidden">{label} </span>
      <span role="timer" className="ac-timer__digits">
        {clock(seconds)}
      </span>
    </p>
  );
}

/** A local countdown for short phases (EO preparation and speaking time). */
export function usePhaseCountdown(seconds: number, running: boolean, onEnd: () => void): number {
  const [left, setLeft] = useState(seconds);
  const end = useRef(onEnd);
  useEffect(() => {
    end.current = onEnd;
  }, [onEnd]);
  useEffect(() => {
    setLeft(seconds);
    if (!running) return;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const rest = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));
      setLeft(rest);
      if (rest === 0) {
        window.clearInterval(timer);
        end.current();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [seconds, running]);
  return left;
}

export function SkillBadge({ skill }: { skill: Skill }) {
  const { t } = useTranslation();
  return <Badge>{t(`academy.skill.${skill}`)}</Badge>;
}
